import { Button, Tag } from "antd";
import { AppWindow, Calculator, LayoutDashboard, ShieldCheck, ShoppingBag } from "lucide-react";

import Modal from "@/components/Modal";

import { RolePermissionLoading, RolePermissionView } from "./RolePermissionView";
import type { RoleDetailModalProps } from "./types";
import { useRoleDetail } from "./useRoleDetail";

export type { RoleDetailModalProps, RoleDetailSource, RolePermOpenParams } from "./types";

/** 系统端编码 → 图标（基本信息区 Tag 用） */
const SYSTEM_TYPE_ICON: Record<string, React.ReactNode> = {
  BIZ: <ShoppingBag className="w-3 h-3" />,
  POS: <Calculator className="w-3 h-3" />,
  WKS: <LayoutDashboard className="w-3 h-3" />,
  OPS: <AppWindow className="w-3 h-3" />,
};

/**
 * 角色权限详情弹窗：支持员工 / 账号 / 角色 ID 三种数据来源
 */
export const RoleDetailModal = ({ open, onClose, roleName, source }: RoleDetailModalProps) => {
  const { data, loading } = useRoleDetail(open, roleName, source);
  const displayName = (data?.roleName ?? roleName.replace(/模板$/, "").trim()) || roleName;

  return (
    <Modal
      open={open}
      width={900}
      title={displayName}
      subtitle="角色权限详情"
      titleIcon={<ShieldCheck className="w-5 h-5 text-orange-500" />}
      footer={
        <div className="flex justify-end w-full">
          <Button onClick={onClose}>关闭</Button>
        </div>
      }
      onCancel={onClose}
    >
      {!source ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>缺少角色查询来源，无法加载权限</p>
        </div>
      ) : loading ? (
        <RolePermissionLoading />
      ) : !data ? (
        <div className="text-center py-8 text-slate-500 text-sm">
          <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>暂无该角色的权限配置信息</p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[65vh] overflow-y-auto">
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500 shrink-0">角色名称</span>
              <span className="font-medium">{displayName}</span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500 shrink-0">所属端</span>
              <div className="flex flex-wrap gap-1.5">
                {data.terminals.length > 0 ? (
                  data.terminals.map((terminal) => (
                    <Tag
                      key={terminal.systemTypeId}
                      bordered
                      className="inline-flex! items-center gap-1 text-xs m-0 py-0.5 leading-none"
                    >
                      <span className="inline-flex shrink-0">
                        {SYSTEM_TYPE_ICON[terminal.systemTypeCode]}
                      </span>
                      {terminal.systemTypeCode}
                    </Tag>
                  ))
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </div>
            </div>
            <div className="flex items-start gap-4 text-sm">
              <span className="text-slate-500 shrink-0">角色描述</span>
              <span className="text-slate-800">{data.roleDesc || "—"}</span>
            </div>
          </div>
          <RolePermissionView data={data} />
        </div>
      )}
    </Modal>
  );
};
